// static/js/components/ui/alert-dialog.jsx
"use client"

/*
 
  The provided JSX code is a complete setup for an alert dialog component using Radix UI’s alert dialog primitives in a React application. The code defines several custom components for different parts of an alert dialog, such as the overlay, content, header, footer, title, description, action buttons, and cancel buttons. Here’s a breakdown and explanation of each part:
    1.	AlertDialog: This is the root component from Radix UI, which encapsulates the entire alert dialog logic.
    2.	AlertDialogTrigger: Component used to trigger the opening of the alert dialog.
    3.	AlertDialogPortal: This component renders the alert dialog into a portal, which is an effective way to manage modals and overlays in React, ensuring they are rendered on top of other content.
    4.	AlertDialogOverlay: A component that renders a semi-transparent overlay behind the alert dialog. It uses forwardRef to allow passing a ref through it, which is useful for handling focus and animations.
    5.	AlertDialogContent: This is the main content container for the alert dialog. It also uses forwardRef and includes complex styling to position the dialog correctly on the screen. It wraps the content inside a portal and an overlay for correct layering and styling.
    6.	AlertDialogHeader, AlertDialogFooter: These components are used for structuring the content inside the alert dialog, providing a consistent layout for headers and footers.
    7.	AlertDialogTitle, AlertDialogDescription: These components are used to display the title and description within the alert dialog, formatted with specific styles.
    8.	AlertDialogAction, AlertDialogCancel: These are button components within the alert dialog for actions like submitting or canceling. They use forwardRef for ref forwarding and are styled for consistency with the dialog’s design.
    9.	ClassName Handling: The cn function is used extensively for class name management, likely a utility to concatenate class names conditionally, which is common in React projects for dynamic styling.
    10.	Export: The module exports all the defined components so they can be used elsewhere in the application.

  Considerations:
    •	Props Spreading: Each component uses the spread syntax (...props) to pass through any additional props it receives. This is a common pattern in React to make components more flexible.
    •	Accessibility: Components like AlertDialogOverlay and AlertDialogContent are likely designed to handle focus management and screen reader accessibility, crucial for modal dialogs.
    •	Styling: The styling uses Tailwind CSS (inferred from the class names and utility approach), which is a utility-first CSS framework. The classes control various aspects such as positioning, spacing, background, border, and transitions.

  Usage:

  These components can be assembled to create a functional alert dialog in a React application. By importing and using these exported components, you can construct dialogs with customized content, headers, footers, and action buttons, handling user interactions effectively.

*/

import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"
import { cn } from "@/lib/utils"

const AlertDialog = AlertDialogPrimitive.Root

const AlertDialogTrigger = AlertDialogPrimitive.Trigger

const AlertDialogPortal = AlertDialogPrimitive.Portal

const AlertDialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName

const AlertDialogContent = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    />
  </AlertDialogPortal>
))
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName

const AlertDialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
AlertDialogHeader.displayName = "AlertDialogHeader"

const AlertDialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
AlertDialogFooter.displayName = "AlertDialogFooter"

const AlertDialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-primary-text", className)}
    {...props}
  />
))
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName

const AlertDialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-primary-text", className)}
    {...props}
  />
))
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName

const AlertDialogAction = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-text ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  />
))
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName

const AlertDialogCancel = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(
      "mt-2 inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-semibold ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0",
      className
    )}
    {...props}
  />
))
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}